import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  Input,
  Box,
  ModalFooter,
  Button,
  Grid,
  Spacer,
  TextArea,
  Switch,
  Text,
} from "@flexisaf/flexibull2";
import { createForm } from "rc-form";
import { useDispatch, useSelector } from "react-redux";

import Theme from "../../../theme";
import {
  selectIsCreatingExam,
  selectIsEditingExam,
} from "../../../redux/exam/selectors";
import {
  createExamStart,
  editExamStart,
  getAllExamsStart,
} from "../../../redux/exam/reducer";
import { selectGroupDetails } from "../../../redux/exam-group/selectors";
import { useParams } from "react-router-dom";
import useSingleExamGet from "../../../hooks/exams/useSingleExamGet";
import SectionLoader from "../../../components/custom/sectionLoader";

const ExamEditor = ({ form, open, data, cbProps, closeModal, refetch }) => {
  const [editData, setEditData] = useState(null);
  const [active, setActive] = useState(false);
  const isEditing = useSelector(selectIsEditingExam);
  const isCreating = useSelector(selectIsCreatingExam);
  const yearLimit = new Date().getFullYear();
  const singleGroup = useSelector(selectGroupDetails);
  const { id } = useParams();
  const { exam, isLoadingExam, resetLoadingExam, refetchExam } =
    useSingleExamGet({
      id: data?.id,
    });

  const dispatch = useDispatch();
  const month = editData?.month + 1;
  const editDate = `${editData?.year?.toString()}-${month
    .toString()
    .padStart(2, "0")}`;

  const { getFieldError, getFieldProps, validateFields, resetFields } = form;

  useEffect(() => {
    if (data?.id && exam) {
      setEditData(exam);
      setActive(exam?.active);
    }
    return () => {
      setEditData(null);
      setActive(false);
    };
  }, [data?.id, exam]);

  const handleClose = () => {
    resetFields();
    resetLoadingExam();
    closeModal();
    document.body.style.overflow = "auto";
  };

  const create = () => {
    validateFields((err, values) => {
      if (!err) {
        const { examName, examMonth, description } = values;
        const year = new Date(examMonth).getFullYear();
        const month = new Date(examMonth).getMonth();
        let payload = {
          data: {
            name: examName,
            description,
            active,
            month,
            year,
            examGroupId: singleGroup?.id,
          },
          callback: () => {
            handleClose();
            if (cbProps?.refetchPapers) {
              refetch();
            }
          },
        };
        dispatch(createExamStart(payload));
      }
    });
  };

  const edit = () => {
    validateFields((err, values) => {
      if (!err) {
        const { examName, examMonth, description } = values;
        const year = new Date(examMonth).getFullYear();
        const month = new Date(examMonth).getMonth();
        let payload = {
          data: {
            name: examName,
            description,
            active,
            month,
            year,
            examGroupId: singleGroup?.id,
            id: editData.id,
          },
          callback: () => {
            if (cbProps?.refetchPapers) {
              dispatch(getAllExamsStart({ examGroupId: id }));
            }
            if (cbProps?.refetchPaper) {
              refetchExam();
            }
            handleClose();
          },
        };

        dispatch(editExamStart(payload));
      }
    });
  };
  return (
    <Modal outerlick onClose={handleClose} open={open}>
      <ModalBody width="40vw">
        {isLoadingExam && data ? (
          <SectionLoader height="60vh" />
        ) : (
          <>
            <Box
              pad="20px"
              style={{ borderBottom: `1px solid ${Theme.PrimaryBorderColor}` }}
            >
              <h3>{`${editData ? "Edit" : "Create New"} Exam For ${
                singleGroup?.name || editData?.examGroup?.name
              }`}</h3>
            </Box>
            <Spacer space={20} />
            <Box pad="2rem">
              <Grid default="1fr 1fr">
                <Input
                  name="examName"
                  placeholder="Ex: January 2021"
                  label="Exam Name"
                  maxLength={32}
                  required
                  error={
                    getFieldError("examName") ? "Exam name is required" : null
                  }
                  {...getFieldProps("examName", {
                    initialValue: editData ? editData?.name : "",
                    rules: [{ required: true }],
                  })}
                />
                <Input
                  type="month"
                  name="examMonth"
                  max={`${yearLimit}-12`}
                  placeholder="Ex: WAEC"
                  label="Exam Period"
                  required
                  error={
                    getFieldError("examMonth")
                      ? "Exam month  is required"
                      : null
                  }
                  {...getFieldProps("examMonth", {
                    initialValue: editData ? editDate : "",
                    rules: [{ required: true }],
                  })}
                />
              </Grid>

              <Spacer space={60} />
              <Grid default="1fr">
                <TextArea
                  name="description"
                  height="100px"
                  block
                  placeholder="Short description of the exam group"
                  label="Description"
                  required
                  error={
                    getFieldError("description")
                      ? "Exam description is required"
                      : null
                  }
                  {...getFieldProps("description", {
                    initialValue: editData ? editData?.description : "",
                    rules: [{ required: true }],
                  })}
                />
              </Grid>
              <Spacer space={30} />
              <Box
                pad="20px 0"
                style={{
                  borderTop: `1px solid ${Theme.PrimaryBorderColor}`,
                }}
              >
                <Text bold>Exam Active</Text>
                <Box />
                <Switch
                  size={20}
                  checked={editData?.isActive || active}
                  onChange={(e) => setActive(e.target.checked ? true : false)}
                  block
                />
              </Box>
            </Box>
            <ModalFooter
              style={{ borderTop: `1px solid ${Theme.PrimaryBorderColor}` }}
            >
              <Box pad="20px">
                <Grid default="1fr 1fr">
                  <Box>
                    <Button pale color="transparent" onClick={handleClose}>
                      Cancel
                    </Button>
                  </Box>
                  <Box align="right">
                    {editData ? (
                      <Button onClick={edit} progress={isEditing}>
                        Edit
                      </Button>
                    ) : (
                      <Button onClick={create} progress={isCreating}>
                        Create
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </ModalFooter>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default createForm()(ExamEditor);
